<template>
  <v-card variant="flat" elevation="0" :loading="loading" :disabled="disabled">
    <FileUpload
      name="FILE[]"
      multiple
      :accept="accept"
      :maxFileSize="maxFileSize"
      :fileLimit="fileLimit"
      customUpload
      @select="show = true"
      @uploader="uploadEvent"
    >
      <template
        #header="{ files, chooseCallback, uploadCallback, clearCallback }"
      >
        <div class="pa-4">
          <v-btn
            @click="chooseCallback()"
            prepend-icon="mdi-plus"
            variant="outlined"
            color="primary"
            :class="$vuetify.display.smAndUp ? 'mr-2' : 'mb-2'"
            :block="$vuetify.display.xs"
            :disabled="files.length >= fileLimit"
            >{{ $t("upload.choose_files") }}</v-btn
          >
          <v-btn
            @click="uploadCallback()"
            prepend-icon="mdi-tray-arrow-up"
            variant="outlined"
            color="primary"
            :class="$vuetify.display.smAndUp ? 'mr-2' : 'mb-2'"
            :block="$vuetify.display.xs"
            :disabled="!files || files.length === 0 || files.length > fileLimit"
            >{{ $t("upload.upload") }}</v-btn
          >
          <v-btn
            @click="clearCallback()"
            prepend-icon="mdi-delete"
            variant="outlined"
            color="primary"
            :block="$vuetify.display.xs"
            :disabled="!files || files.length === 0"
            >{{ $t("upload.clear") }}</v-btn
          >
        </div>
      </template>

      <template #content="{ files, removeFileCallback, messages }">
        <div
          :class="$vuetify.display.smAndUp ? 'px-10' : 'px-1'"
          v-if="messages"
        >
          <p class="text-red" v-for="(message, index) in messages" :key="index">
            <v-icon>mdi-alert-circle-outline</v-icon> {{ message }}
          </p>
        </div>

        <div
          :class="$vuetify.display.smAndUp ? 'px-10 pb-10' : 'px-1 pb-1'"
          v-if="files.length > 0"
        >
          <v-row>
            <v-col v-for="(file, index) of files" :key="index">
              <v-card
                variant="outlined"
                elevation="0"
                class="pa-3"
                min-width="200"
              >
                <div class="text-center">
                  <a
                    :href="objectURL(file)"
                    target="_blank"
                    v-if="file.type == 'application/pdf'"
                  >
                    <v-btn
                      class="mb-1 float-right"
                      color="error"
                      variant="outlined"
                      prepend-icon="mdi-file-pdf-box"
                      elevation="0"
                    >
                      {{ $t("upload.view_file") }}
                    </v-btn>
                  </a>

                  <embed
                    :src="objectURL(file)"
                    width="100%"
                    height="350px"
                    type="application/pdf"
                    v-if="file.type == 'application/pdf'"
                  />
                  <PrimeVueImage :src="file.objectURL" width="100%" v-else />
                </div>
                <p class="text-center">{{ file.type }}</p>
                <p class="text-center">{{ formatSize(file.size) }}</p>
                <div class="text-center pt-1 pb-2">
                  <v-chip class="text-center" color="warning">
                    {{ $t("upload.pending") }}
                  </v-chip>
                </div>
                <div class="text-center">
                  <v-btn
                    @click="removeFileCallback(index)"
                    icon="mdi-delete"
                    variant="outlined"
                    color="error"
                    size="small"
                  ></v-btn>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </template>

      <template #empty>
        <div class="align-items-center justify-content-center py-5">
          <p class="text-center pb-5">
            <v-icon icon="mdi-file-plus-outline" size="100"></v-icon>
          </p>
          <p class="text-center">{{ $t("upload.drag_and_drop") }}</p>
          <p class="text-center">
            {{ $t("upload.support_file_types") }}: <b>{{ accept }}</b>
          </p>
          <p class="text-center">
            {{ $t("upload.max_file_size") }}:
            <b>{{ formatSize(maxFileSize) }}</b>
          </p>
          <p class="text-center">
            {{ $t("upload.max_num_files") }}: <b>{{ fileLimit }}</b>
          </p>
        </div>
      </template>
    </FileUpload>
  </v-card>

  <v-dialog v-model="show" persistent eager max-width="350">
    <v-card>
      <v-toolbar color="warning" class="ps-4"
        ><h3>{{ $t("upload.alert") }}</h3></v-toolbar
      >

      <v-card-text
        ><p>{{ $t("upload.alert_text") }}</p></v-card-text
      >

      <v-card-actions class="pb-5 pr-4">
        <v-spacer></v-spacer>
        <v-btn @click="show = false" variant="outlined">
          {{ $t("upload.close") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import FileUpload from "primevue/fileupload";
import PrimeVueImage from "@/components/info_cards/PrimeVueImage.vue";

export default {
  name: "PrimeVueUpload",

  components: {
    FileUpload,
    PrimeVueImage,
  },

  props: {
    accept: {
      type: String,
    },
    maxFileSize: {
      type: Number,
      default: 4 * 1024 * 1024,
    },
    fileLimit: {
      type: Number,
      default: 1,
    },
    callback: {
      type: Function,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    loading: false,
    show: false,
  }),

  methods: {
    objectURL(file) {
      return window.URL.createObjectURL(file);
    },

    formatSize(bytes) {
      const k = 1024;
      const dm = 2;
      const sizes = this.$primevue.config.locale.fileSizeTypes;

      if (bytes === 0) {
        return `0 ${sizes[0]}`;
      }

      const i = Math.floor(Math.log(bytes) / Math.log(k));
      const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

      return `${formattedSize} ${sizes[i]}`;
    },

    async uploadEvent(event) {
      this.loading = true;
      // await new Promise((resolve) => setTimeout(resolve, 1000));
      this.callback(event.files);
      this.loading = false;
    },
  },
};
</script>

<style>
.p-fileupload .p-fileupload-buttonbar {
  background-color: white;
  border: 0px;
}

.p-fileupload .p-fileupload-content {
  background-color: white;
  border: 0px;
}

.p-fileupload-buttonbar {
  display: block;
}

.v-card--variant-outlined {
  border: thin solid #ddd !important;
}
</style>
